import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, Package, Wand2, ShoppingBag, Store, Crown, AlertTriangle } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { Loading } from "@/components/RequireAuth";
import { StatusBadge } from "@/components/Badges";
import { fmtDate } from "@/lib/api";

const NAV = [
  ["/vendeur", "Aperçu", LayoutDashboard, "overview"],
  ["/vendeur/ia", "Créer avec l'IA", Wand2, "ai"],
  ["/vendeur/produits", "Produits", Package, "products"],
  ["/vendeur/commandes", "Commandes", ShoppingBag, "orders"],
  ["/vendeur/boutique", "Boutique & livraison", Store, "shop"],
  ["/vendeur/abonnement", "Abonnement", Crown, "subscription"],
];

const EXPIRY_REMINDERS = [3, 7];

const expiryNotice = (shop) => {
  if (!shop.subscription_end) return null;
  const days = Math.ceil((new Date(shop.subscription_end) - Date.now()) / 86400000);
  const date = fmtDate(shop.subscription_end);
  const common = { link: "/vendeur/abonnement", linkLabel: "Renouveler" };
  if (shop.subscription_kind === "offert") {
    if (days <= 0) return { ...common, linkLabel: "Choisir une formule", tone: "red", testid: "subscription-expired-notice", text: `Votre accès offert s'est terminé le ${date} : votre boutique n'apparaît plus dans le catalogue. Choisissez une formule pour continuer ; elle sera activée après vérification manuelle du paiement.` };
    if (days <= EXPIRY_REMINDERS[1]) return { ...common, linkLabel: "Choisir une formule", tone: "amber", testid: "offer-expiry-notice", text: `Votre accès offert par Casadeal se termine dans ${days} jour${days > 1 ? "s" : ""} (le ${date}). Pour continuer ensuite, choisissez une formule payante.` };
    return null;
  }
  if (days <= 0) return { ...common, tone: "red", testid: "subscription-expired-notice", text: `Votre abonnement a expiré le ${date} : votre boutique n'apparaît plus dans le catalogue. Faites une demande de renouvellement ; elle sera activée après vérification manuelle du paiement.` };
  const plural = `${days} jour${days > 1 ? "s" : ""}`;
  if (days <= EXPIRY_REMINDERS[0]) return { ...common, tone: "orange", testid: "subscription-expiry-3days-notice", text: `Rappel : votre abonnement se termine dans ${plural} (le ${date}). Demandez votre renouvellement dès maintenant et contactez Casadeal pour le paiement : l'activation n'est jamais automatique.` };
  if (days <= EXPIRY_REMINDERS[1]) return { ...common, tone: "amber", testid: "subscription-expiry-7days-notice", text: `Rappel : votre abonnement se termine dans ${plural} (le ${date}). Pensez à le renouveler à temps : le paiement est vérifié manuellement avant activation.` };
  return null;
};

const ShopNotice = ({ shop }) => {
  if (shop.status === "pending") return <Notice tone="amber" text="Votre demande est en cours d'examen par l'administrateur. Vous pouvez préparer vos produits, ils seront visibles après validation et activation d'un abonnement." />;
  if (shop.status === "rejected") return <Notice tone="red" text={`Demande refusée${shop.rejection_reason ? ` : ${shop.rejection_reason}` : ""}. Vous pouvez soumettre une nouvelle demande.`} link="/devenir-vendeur" />;
  if (shop.status === "suspended") return <Notice tone="red" text={`Boutique suspendue${shop.rejection_reason ? ` : ${shop.rejection_reason}` : ""}. Elle n'apparaît plus dans le catalogue.`} />;
  if (!shop.selling_enabled) return <Notice tone="amber" text="La vente n'est pas encore ouverte dans le pays de votre boutique : elle n'apparaît pas dans le catalogue public. Bientôt disponible." />;
  const exp = expiryNotice(shop);
  if (exp) return <Notice {...exp} />;
  if (!shop.visible) return <Notice tone="amber" text="Boutique approuvée mais sans abonnement actif : elle n'apparaît pas dans le catalogue public." link="/vendeur/abonnement" />;
  return null;
};

const TONES = { red: "bg-red-50 text-red-800 ring-red-200", orange: "bg-orange-100 text-orange-900 ring-orange-300", amber: "bg-amber-50 text-amber-900 ring-amber-200" };

const Notice = ({ tone, text, link, linkLabel = "Voir", testid = "shop-notice" }) => (
  <div data-testid={testid} className={`mb-6 flex items-start gap-3 rounded-xl p-4 text-sm ring-1 ${TONES[tone]}`}>
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><p className="flex-1">{text}</p>
    {link && <Link to={link} data-testid={`${testid}-link`} className="font-semibold underline">{linkLabel}</Link>}
  </div>
);

export default function SellerLayout() {
  const { data: shop, reload } = useApi("/seller/shop");
  if (!shop) return <Loading />;
  return (
    <div data-testid="seller-layout" className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-6 md:grid-cols-[230px_1fr] md:px-6">
      <aside className="min-w-0 md:sticky md:top-24 md:h-fit">
        <div className="mb-4 rounded-xl border bg-white p-4">
          <p className="truncate font-display text-lg font-bold">{shop.name}</p>
          <div className="mt-1 flex items-center gap-2"><StatusBadge status={shop.status} kind="shop" testid="seller-shop-status" /></div>
          {shop.subscription_end && new Date(shop.subscription_end) > new Date() && <p className="mt-2 text-xs text-gray-500">{shop.subscription_kind === "offert" ? "Accès offert" : "Abonnement"} jusqu'au {fmtDate(shop.subscription_end)}</p>}
        </div>
        <nav className="no-scrollbar -mx-4 flex gap-1 overflow-x-auto px-4 md:mx-0 md:flex-col md:px-0">
          {NAV.map(([to, label, I, id]) => (
            <NavLink key={to} to={to} end={to === "/vendeur"} data-testid={`seller-nav-${id}`}
              className={({ isActive }) => `flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-orange-500 text-white" : "text-gray-700 hover:bg-orange-50"} ${id === "ai" ? "ring-1 ring-indigo-200" : ""}`}>
              <I className="h-4 w-4" />{label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <section className="min-w-0">
        <ShopNotice shop={shop} />
        <Outlet context={{ shop, reloadShop: reload }} />
      </section>
    </div>
  );
}
